/* =======================================================================
   BUNCH - CART DRAWER "is-empty" STATE SYNC                (added 2026-08-04)
   ==========================================================================
   THE BUG
   -------
   Adding the FIRST item to an empty cart produced a drawer with:
     - no close button
     - no product lines visible
     - only the estimated total and the checkout button
     - content clipped on the left, upsell cards cut off at both edges

   ROOT CAUSE
   ----------
   Two halves of the theme disagree about WHICH element carries `is-empty`.

   snippets/cart-drawer.liquid puts it on the HOST element:

       <cart-drawer class="drawer is-empty">

   and every stylesheet is written to match that:

       cart-drawer.is-empty .drawer__header { display: none }
       cart-drawer.is-empty .drawer__inner  { display: grid; align-items: center }
       .is-empty .cart__contents            { display: none }

   But Dawn's assets/cart-drawer.js clears it from a DIFFERENT element:

       renderContents(parsedState) {
         this.querySelector(".drawer__inner").classList.contains("is-empty") &&
         this.querySelector(".drawer__inner").classList.remove("is-empty");
         ...
       }

   `.drawer__inner` never has the class, so the removal is a no-op and the
   class stays on <cart-drawer> forever. The Section Rendering API replaces
   the drawer's INNER html; it does not touch classes on the host element.

   MEASURED ON THIS THEME, cart 0 -> 1 item via the real "Add to bag":

     /cart.js item_count .............. 1
     .cart-item rows in the drawer .... 1        (the items ARE rendered)
     <cart-drawer> classes ............ drawer animate is-empty active  <-- stale
     .drawer__header display .......... none     -> no close button
     .drawer__close rendered width .... 0px
     .cart__contents .................. display:none  -> products invisible

   `align-items: center` in that same is-empty rule is what made the drawer's
   children shrink-to-fit and centre, which is why headings and the upsell row
   appeared clipped on both edges.

   WHY IT ONLY HIT REAL SHOPPERS
   -----------------------------
   It requires the cart to START empty. Any session that already had a line
   item rendered the host without `is-empty`, so the drawer behaved normally.
   That makes it the first add-to-cart of every new visitor - the single most
   important interaction on the store - while being invisible to anyone
   testing with a cart that already had something in it.

   THE FIX
   -------
   Wrap renderContents and re-derive the host class from the markup Shopify
   just returned. `cart-drawer-items` carries an authoritative `is-empty`
   class from the server on every section render, so it is the source of
   truth, with a line-item count as a fallback.

   Dawn's original method still runs first and its own `.drawer__inner`
   cleanup is left untouched, so if a future theme update moves the class
   back to `.drawer__inner` nothing here breaks.
   ========================================================================== */
(function () {
  function sync(drawer) {
    if (!drawer) return;
    var items = drawer.querySelector('cart-drawer-items');
    var rows = drawer.querySelectorAll('.cart-item').length;
    var isEmpty = items ? items.classList.contains('is-empty') : rows === 0;
    if (!items && rows === 0) isEmpty = true;
    drawer.classList.toggle('is-empty', isEmpty);
  }

  function patch() {
    var CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype.__bunchEmptySync) return;

    var original = CartDrawer.prototype.renderContents;
    if (typeof original !== 'function') return;

    CartDrawer.prototype.renderContents = function (parsedState) {
      var result = original.call(this, parsedState);
      try { sync(this); } catch (e) { /* never block the cart */ }
      return result;
    };

    CartDrawer.prototype.__bunchEmptySync = true;

    /* Correct any drawer already in the DOM at load time. */
    var list = document.querySelectorAll('cart-drawer');
    for (var i = 0; i < list.length; i++) sync(list[i]);
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(patch);
  } else {
    document.addEventListener('DOMContentLoaded', patch);
  }
})();
